import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Switch,
  Upload,
  Image,
  Modal,
  Space,
  TimePicker
} from "antd";
import moment from "moment";
import { UploadOutlined, PlusOutlined } from "@ant-design/icons";
import ReactMapboxGl, { Marker } from "react-mapbox-gl";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import axios from "axios";
import request from "helper/request";
import { setMapboxRTLTextPlugin } from "../../../../helper/util";
import { DEFAULT_CENTER } from "../../../../configs/constants";
import AddressInput from "components/inputs/AddressInput";
import { selectCurrentLocation, clearCurrentLocation } from "../store/slice";
import { getLocationById, updateLocation } from "../store/thunks";
import clientApi from "../../clients/store/api";
import "../styles/locations.scss";

const { TextArea } = Input;

mapboxgl.workerClass =
  require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;
setMapboxRTLTextPlugin(mapboxgl);

const Map = ReactMapboxGl({
  accessToken: window.REACT_APP_MAPBOX_KEY
});

const AdminLocationDetail = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { locationId } = useParams();
  const isCreateMode = locationId === "new";
  const currentLocation = useSelector(selectCurrentLocation);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [locationImage, setLocationImage] = useState(null);

  useEffect(() => {
    if (locationId && !isCreateMode) {
      dispatch(getLocationById(locationId));
    } else if (isCreateMode) {
      // Initialize empty state for create mode
      dispatch(clearCurrentLocation());
    }

    return () => {
      dispatch(clearCurrentLocation());
    };
  }, [dispatch, locationId, isCreateMode]);

  // Load all clients for the dropdown
  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await clientApi.getClientList({ page: 1, pageSize: 1000 });
        console.log("Clients API response:", response);
        console.log("Clients data:", response.data);
        console.log("Clients array:", response.data?.data);

        // Handle different possible response structures
        const clients = response.data?.data || response.data || response || [];
        console.log("Setting clients:", clients);
        setAllClients(clients);
      } catch (error) {
        console.error("Error loading clients:", error);
      }
    };
    loadClients();
  }, []);

  useEffect(() => {
    if (isCreateMode) {
      // Initialize empty form for create mode
      form.setFieldsValue({
        locationName: "",
        address: "",
        descriptiveAddress: "",
        lat: null,
        lng: null,
        contactName: "",
        contactPhone: "",
        contactEmail: "",
        timeWindowStart: moment("08:00", "HH:mm"),
        timeWindowEnd: moment("17:00", "HH:mm"),
        waitingTime: 0,
        note: "",
        internalCustomerId: "",
        salesperson: "",
        clientId: "",
        isPublic: false
      });
    } else if (currentLocation && Object.keys(currentLocation).length > 0) {
      form.setFieldsValue({
        locationName: currentLocation.locationName,
        address: currentLocation.address,
        descriptiveAddress: currentLocation.descriptiveAddress,
        lat: parseFloat(currentLocation.lat),
        lng: parseFloat(currentLocation.lng),
        contactName: currentLocation.contactName,
        contactPhone: currentLocation.contactPhone,
        contactEmail: currentLocation.contactEmail,
        timeWindowStart: currentLocation.timeWindowStart
          ? moment(currentLocation.timeWindowStart, "HH:mm")
          : null,
        timeWindowEnd: currentLocation.timeWindowEnd
          ? moment(currentLocation.timeWindowEnd, "HH:mm")
          : null,
        waitingTime: currentLocation.waitingTime,
        note: currentLocation.note,
        internalCustomerId: currentLocation.internalCustomerId,
        salesperson: currentLocation.salesperson,
        clientId: currentLocation.clientId,
        clientName: currentLocation.clientName,
        isPublic: currentLocation.isPublic
      });

      // Initialize marker position
      if (currentLocation.lat && currentLocation.lng) {
        setMarkerPosition([
          parseFloat(currentLocation.lng),
          parseFloat(currentLocation.lat)
        ]);
      }

      // Set location image if available
      if (currentLocation.image) {
        // Assuming the image field contains a URL or file ID
        setLocationImage(currentLocation.image);
      }
    }
  }, [currentLocation, form, isCreateMode]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const locationData = {
        ...values,
        contactName: values.contactName?.trim() || null,
        contactPhone: values.contactPhone?.trim(),
        contactEmail: values.contactEmail?.trim() || null,
        note: values.note?.trim() || null,
        descriptiveAddress: values.descriptiveAddress?.trim() || null,
        internalCustomerId: values.internalCustomerId?.trim() || null,
        salesperson: values.salesperson?.trim() || null,
        timeWindowStart: values.timeWindowStart
          ? values.timeWindowStart.format("HH:mm")
          : null,
        timeWindowEnd: values.timeWindowEnd ? values.timeWindowEnd.format("HH:mm") : null,
        waitingTime: parseInt(values.waitingTime) || 0
      };

      if (isCreateMode) {
        // Create new location
        await request.post(`/admin/saved-locations`, locationData);
        message.success("Location created successfully");
        // Redirect to the locations list
        history.push("/admin/locations");
      } else {
        // Update existing location
        await dispatch(
          updateLocation({
            locationId,
            ...locationData
          })
        ).unwrap();

        message.success("Location updated successfully");

        // Reload the location data to ensure form reflects saved state
        await dispatch(getLocationById(locationId));
      }
    } catch (error) {
      message.error(
        isCreateMode ? "Failed to create location" : "Failed to update location"
      );
      console.error("Save error:", error);
    } finally {
      setLoading(false);
    }
  };

  const _onClickMap = (map, e) => {
    const lngLatClick = e.lngLat;
    const newPosition = [lngLatClick.lng, lngLatClick.lat];
    setMarkerPosition(newPosition);

    axios
      .get(
        `${window.REACT_APP_API_ENDPOINT}/google/googlegeo?lat=${lngLatClick.lat}&lng=${lngLatClick.lng}`
      )
      .then((res) => {
        // Update form fields with new coordinates and address
        form.setFieldsValue({
          lat: lngLatClick.lat,
          lng: lngLatClick.lng,
          address: res.data.results[0].formatted_address
        });
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const onMapLoad = (map) => {
    map.addControl(new mapboxgl.FullscreenControl(), "top-right");
  };

  const handleClickRef = useRef(_onClickMap);
  handleClickRef.current = _onClickMap;

  const handleBack = () => {
    history.push("/admin/locations");
  };

  const handleAddressSelect = (addrObj) => {
    form.setFieldsValue({
      address: addrObj.address,
      lat: addrObj.lngLat[1],
      lng: addrObj.lngLat[0]
    });
    setMarkerPosition([addrObj.lngLat[0], addrObj.lngLat[1]]);
  };

  const validateImageFileType = (file) => {
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/heic"
    ];
    return allowedTypes.includes(file.type.toLowerCase());
  };

  const validateImageFileSize = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    return file.size <= maxSize;
  };

  const beforeImageUpload = (file) => {
    if (!validateImageFileType(file)) {
      message.error("Only JPG, JPEG, PNG, WebP, and HEIC image files are allowed");
      return Upload.LIST_IGNORE;
    }

    if (!validateImageFileSize(file)) {
      message.error("File size must be less than 5MB");
      return Upload.LIST_IGNORE;
    }

    setSelectedImageFile(file);
    setShowImageUploadModal(true);
    return false; // Prevent automatic upload
  };

  const handleImageUpload = async () => {
    if (!selectedImageFile) {
      message.error("Please select an image file");
      return;
    }

    if (isCreateMode) {
      message.warning("Please save the location first before uploading an image");
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("image", selectedImageFile);

      const response = await request.post(
        `/admin/saved-locations/${locationId}/image`,
        formData
      );
      message.success("Location image uploaded successfully!");

      // Update the image display with the new image URL
      setLocationImage(response.url);

      // Reset modal state
      setShowImageUploadModal(false);
      setSelectedImageFile(null);
    } catch (error) {
      console.error("Error uploading image:", error);
      message.error("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  if (!isCreateMode && (!currentLocation || Object.keys(currentLocation).length === 0)) {
    return <div>Loading...</div>;
  }

  return (
    <div className="admin-location-detail">
      <div className="location-detail-header">
        <div className="breadcrumbs">
          <span
            className="breadcrumb-item"
            onClick={handleBack}
            style={{ cursor: "pointer" }}
          >
            LOCATIONS
          </span>
          <span className="breadcrumb-item">
            {isCreateMode ? "New Location" : currentLocation.locationName}
          </span>
        </div>
        <Button
          type="primary"
          className="save-button"
          onClick={handleSave}
          loading={loading}
        >
          {isCreateMode ? "Create" : "Save"}
        </Button>
      </div>

      <div className="detail-content">
        <div className="form-section">
          <Form form={form} layout="vertical" className="location-form">
            {!isCreateMode && (
              <div className="form-group">
                <div className="form-label">{currentLocation.locationName}</div>
                <div className="form-address">{currentLocation.address}</div>
                {currentLocation.lat && currentLocation.lng && (
                  <div className="coordinates">
                    Lat: {parseFloat(currentLocation.lat).toFixed(6)}, Lng:{" "}
                    {parseFloat(currentLocation.lng).toFixed(6)}
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <Form.Item
                label="Location Name"
                name="locationName"
                rules={[{ required: true, message: "Please enter location name" }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Map Address"
                name="address"
                rules={[{ required: true, message: "Please enter map address" }]}
              >
                <AddressInput
                  placeholder="Enter address"
                  onSelect={handleAddressSelect}
                  address={{ address: form.getFieldValue("address") }}
                />
              </Form.Item>

              <Form.Item label="Physical Address (Descriptive)" name="descriptiveAddress">
                <Input placeholder="e.g., Corner of 4th and Main Street" />
              </Form.Item>

              <div style={{ display: "flex", gap: "16px" }}>
                <Form.Item
                  label="Latitude"
                  name="lat"
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: "Please enter latitude" }]}
                >
                  <InputNumber style={{ width: "100%" }} precision={8} step={0.000001} />
                </Form.Item>

                <Form.Item
                  label="Longitude"
                  name="lng"
                  style={{ flex: 1 }}
                  rules={[{ required: true, message: "Please enter longitude" }]}
                >
                  <InputNumber style={{ width: "100%" }} precision={8} step={0.000001} />
                </Form.Item>
              </div>
            </div>

            <div className="form-group">
              <Form.Item label="Contact Name" name="contactName">
                <Input />
              </Form.Item>

              <Form.Item
                label="Contact Phone"
                name="contactPhone"
                rules={[{ required: true, message: "Please enter contact phone" }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%"
                    }}
                  >
                    <span>Contact Email</span>
                    <span style={{ fontWeight: 400, color: "#888888" }}>(Optional)</span>
                  </div>
                }
                name="contactEmail"
                rules={[{ type: "email", message: "Please enter valid email" }]}
              >
                <Input />
              </Form.Item>
            </div>

            <div className="form-group">
              <div style={{ display: "flex", gap: "16px" }}>
                <Form.Item
                  label="Time Window Start"
                  name="timeWindowStart"
                  style={{ flex: 1 }}
                >
                  <TimePicker
                    format="HH:mm"
                    style={{ width: "100%" }}
                    placeholder="Select time"
                    showNow={false}
                  />
                </Form.Item>

                <Form.Item
                  label="Time Window End"
                  name="timeWindowEnd"
                  style={{ flex: 1 }}
                >
                  <TimePicker
                    format="HH:mm"
                    style={{ width: "100%" }}
                    placeholder="Select time"
                    showNow={false}
                  />
                </Form.Item>
              </div>

              <Form.Item label="Waiting Time (minutes)" name="waitingTime">
                <InputNumber style={{ width: "100%" }} min={0} precision={0} />
              </Form.Item>
            </div>

            <div className="form-group">
              <Form.Item
                label="Client"
                name={isCreateMode ? "clientId" : "clientName"}
                rules={
                  isCreateMode
                    ? [{ required: true, message: "Please select a client" }]
                    : []
                }
              >
                <Select
                  disabled={!isCreateMode}
                  showSearch
                  options={(() => {
                    console.log("allClients state:", allClients);
                    console.log("isCreateMode:", isCreateMode);
                    const options = allClients.map((client) => {
                      console.log("Mapping client:", client);
                      return {
                        label: client.clientName,
                        value: isCreateMode ? client.id : client.clientName
                      };
                    });
                    console.log("Select options:", options);
                    return options;
                  })()}
                  placeholder={isCreateMode ? "Select a client" : "No client selected"}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item label="Internal Customer ID" name="internalCustomerId">
                <Input />
              </Form.Item>

              <Form.Item label="Salesperson" name="salesperson">
                <Input />
              </Form.Item>

              <Form.Item label="Notes" name="note">
                <TextArea rows={4} />
              </Form.Item>
            </div>

            <div className="form-group">
              <Form.Item label="Public Location" name="isPublic" valuePropName="checked">
                <Switch checkedChildren="Public" unCheckedChildren="Private" />
              </Form.Item>

              {!isCreateMode && (
                <Form.Item label="Location Image">
                  <div style={{ marginBottom: 16 }}>
                    <div
                      style={{
                        width: 200,
                        height: 200,
                        border: "2px dashed #d9d9d9",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#fafafa",
                        overflow: "hidden",
                        marginBottom: 8
                      }}
                    >
                      {locationImage ? (
                        <Image
                          src={locationImage}
                          alt="Location Image"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            display: "block"
                          }}
                          preview={true}
                        />
                      ) : (
                        <div style={{ textAlign: "center", color: "#8c8c8c" }}>
                          <PlusOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                          <div>No image uploaded</div>
                          <div style={{ fontSize: "12px" }}>Upload location image</div>
                        </div>
                      )}
                    </div>

                    <Upload
                      beforeUpload={beforeImageUpload}
                      showUploadList={false}
                      accept=".jpg,.jpeg,.png,.webp,.heic"
                    >
                      <Button icon={<UploadOutlined />} type="default">
                        {locationImage ? "Change Image" : "Upload Image"}
                      </Button>
                    </Upload>
                    <div style={{ marginTop: 8, fontSize: "12px", color: "#8c8c8c" }}>
                      Supported formats: JPG, JPEG, PNG, WebP, HEIC
                      <br />
                      Maximum size: 5MB
                    </div>
                  </div>
                </Form.Item>
              )}
              {isCreateMode && (
                <div
                  style={{
                    marginTop: 16,
                    padding: 12,
                    backgroundColor: "#e6f7ff",
                    borderRadius: 4
                  }}
                >
                  <p style={{ margin: 0, fontSize: "14px", color: "#1890ff" }}>
                    Note: You can upload a location image after creating the location.
                  </p>
                </div>
              )}
            </div>
          </Form>
        </div>

        <div className="map-section">
          <div className="map-container" style={{ height: "400px", width: "100%" }}>
            {isCreateMode ||
            (currentLocation && currentLocation.lat && currentLocation.lng) ? (
              <Map
                center={
                  markerPosition ||
                  (isCreateMode
                    ? DEFAULT_CENTER
                    : [parseFloat(currentLocation.lng), parseFloat(currentLocation.lat)])
                }
                style="mapbox://styles/mapbox/streets-v11"
                zoom={[15]}
                onStyleLoad={onMapLoad}
                onClick={(map, event) => handleClickRef.current(map, event)}
                containerStyle={{
                  height: "100%",
                  width: "100%"
                }}
              >
                {markerPosition && (
                  <Marker coordinates={markerPosition}>
                    <img
                      style={{ width: "30px", height: "30px" }}
                      src="/icons/location.png"
                      alt="Location marker"
                    />
                  </Marker>
                )}
              </Map>
            ) : (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #d9d9d9"
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <img
                    src="/icons/map.svg"
                    alt="Map"
                    style={{
                      width: "64px",
                      height: "64px",
                      marginBottom: "16px",
                      opacity: 0.5
                    }}
                  />
                  <div style={{ fontSize: "16px", color: "#999" }}>
                    No coordinates available
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      <Modal
        title="Upload Location Image"
        open={showImageUploadModal}
        onCancel={() => {
          setShowImageUploadModal(false);
          setSelectedImageFile(null);
        }}
        width={500}
        footer={null}
      >
        <div style={{ marginBottom: 16 }}>
          <p>
            <strong>File Details:</strong>
          </p>
          {selectedImageFile && (
            <div
              style={{
                padding: 12,
                backgroundColor: "#f5f5f5",
                borderRadius: 4,
                marginBottom: 16
              }}
            >
              <div>
                <strong>Name:</strong> {selectedImageFile.name}
              </div>
              <div>
                <strong>Size:</strong> {(selectedImageFile.size / 1024).toFixed(1)} KB
              </div>
              <div>
                <strong>Type:</strong> {selectedImageFile.type}
              </div>
            </div>
          )}

          <div
            style={{
              padding: 12,
              backgroundColor: "#e6f7ff",
              borderRadius: 4,
              marginBottom: 16,
              fontSize: "12px"
            }}
          >
            <p style={{ margin: 0, marginBottom: 8 }}>
              <strong>Allowed file types:</strong>
            </p>
            <p style={{ margin: 0 }}>
              • Images: JPG, JPEG, PNG, WebP, HEIC
              <br />• Max size: 5MB
            </p>
          </div>
        </div>

        <div style={{ textAlign: "center" }}>
          <Space>
            <Button
              onClick={() => {
                setShowImageUploadModal(false);
                setSelectedImageFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={uploadingImage}
              disabled={!selectedImageFile}
              onClick={handleImageUpload}
              icon={<UploadOutlined />}
            >
              {uploadingImage ? "Uploading..." : "Upload Image"}
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default AdminLocationDetail;
